import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Section, Eyebrow } from "@/components/Primitives";
import { memberLogin, memberMe, completeMemberModule } from "@/lib/api";

const TOKEN_KEY = "tenet_member_token";
const fieldClass =
  "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#1F3D2E]/40";

function ModuleCard({ m, token, onCompleted }) {
  const [busy, setBusy] = useState(false);
  const complete = async () => {
    setBusy(true);
    try {
      const res = await completeMemberModule(token, m.id);
      toast.success("Module complete — the next one is unlocked.");
      onCompleted(res.modules);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update your progress.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={`member-module-${m.id}`}>
      <div className="flex items-center justify-between px-5 pt-5">
        <p className="overline-label">Module {m.number}</p>
        {m.completed ? (
          <span className="text-xs font-semibold text-[#4A7C59] flex items-center gap-1.5" data-testid={`member-status-${m.id}`}>
            <i className="fa-solid fa-circle-check" /> Completed
          </span>
        ) : !m.unlocked ? (
          <span className="text-xs font-semibold text-stone-400 flex items-center gap-1.5" data-testid={`member-status-${m.id}`}>
            <i className="fa-solid fa-lock" /> Locked
          </span>
        ) : m.coming_soon ? (
          <span className="text-xs font-semibold text-amber-700 flex items-center gap-1.5" data-testid={`member-status-${m.id}`}>
            <i className="fa-solid fa-clock" /> Coming soon
          </span>
        ) : (
          <span className="text-xs font-semibold text-[#1F3D2E] flex items-center gap-1.5" data-testid={`member-status-${m.id}`}>
            <i className="fa-solid fa-play" /> Ready to watch
          </span>
        )}
      </div>
      <div className="p-5 pt-2">
        <h3 className="font-playfair text-lg text-stone-900">{m.title}</h3>
        <p className="text-sm text-stone-500 mt-1 leading-relaxed">{m.description}</p>

        {m.unlocked && m.video_url && (
          <div className="mt-4">
            <video
              controls
              preload="metadata"
              className="w-full aspect-video bg-black rounded-lg"
              data-testid={`member-video-${m.id}`}
            >
              <source src={m.video_url} type="video/mp4" />
              Your browser doesn't support embedded video.
            </video>
            {!m.completed && (
              <button
                onClick={complete}
                disabled={busy}
                data-testid={`member-complete-${m.id}`}
                className="btn-terracotta rounded-full px-5 py-2.5 text-sm font-semibold mt-4 disabled:opacity-60"
              >
                {busy ? "Saving\u2026" : "Mark as complete"}
              </button>
            )}
          </div>
        )}

        {m.unlocked && m.coming_soon && (
          <div className="mt-4 rounded-xl bg-[#F5F3EE] border border-stone-200 px-4 py-6 text-center text-sm text-stone-500" data-testid={`member-comingsoon-${m.id}`}>
            This module will be added here soon.
          </div>
        )}

        {!m.unlocked && (
          <div className="mt-4 rounded-xl bg-stone-50 border border-stone-200 px-4 py-6 text-center text-sm text-stone-400" data-testid={`member-locked-${m.id}`}>
            Complete the previous module to unlock this one.
          </div>
        )}
      </div>
    </div>
  );
}

export default function MemberArea() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [member, setMember] = useState(null);
  const [modules, setModules] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    memberMe(token)
      .then((d) => {
        setMember(d.member);
        setModules(d.modules);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setMember(null);
      })
      .finally(() => setChecking(false));
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return toast.error("Please enter your email and password.");
    setLoading(true);
    try {
      const res = await memberLogin({ email, password });
      localStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
      setMember(res.member);
      setModules(res.modules);
      toast.success(`Welcome back, ${res.member.name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign in. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMember(null);
    setModules([]);
    setEmail("");
    setPassword("");
  };

  if (checking) {
    return (
      <Section className="pt-24 pb-24">
        <p className="text-stone-500" data-testid="member-checking">Loading…</p>
      </Section>
    );
  }

  if (!member) {
    return (
      <Section className="pt-16 sm:pt-24 pb-24 max-w-md" data-testid="member-login-page">
        <Eyebrow>Member area</Eyebrow>
        <h1 className="font-serif-display text-4xl text-stone-900 mt-4">Welcome back</h1>
        <p className="text-stone-600 mt-3 leading-relaxed">
          Sign in with the email and password we sent you to continue your modules.
        </p>
        <form onSubmit={login} className="space-y-4 mt-8 bg-white rounded-[2rem] border border-stone-200 p-8 shadow-sm" data-testid="member-login-form">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">Email address</label>
            <input data-testid="member-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={fieldClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">Password</label>
            <input data-testid="member-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className={fieldClass} />
          </div>
          <button type="submit" disabled={loading} data-testid="member-login-submit" className="btn-terracotta w-full rounded-full px-6 py-3.5 text-sm font-semibold disabled:opacity-60">
            {loading ? "Signing in\u2026" : "Sign in"}
          </button>
          <p className="text-xs text-stone-400 text-center">
            Don't have access yet? Replies to your welcome email reach us directly.
          </p>
        </form>
      </Section>
    );
  }

  const completedCount = modules.filter((m) => m.completed).length;

  return (
    <div className="pb-8" data-testid="member-area-page">
      <Section className="pt-14 sm:pt-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Member area</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-stone-900 leading-[1.08] mt-4">Your modules</h1>
            <p className="text-stone-600 mt-3" data-testid="member-progress">
              Signed in as {member.email} &middot; {completedCount} of {modules.length} complete
            </p>
          </div>
          <button onClick={signOut} data-testid="member-signout" className="text-sm font-semibold text-[#1F3D2E] rounded-full border border-stone-300 px-4 py-2 hover:bg-stone-50">
            Sign out
          </button>
        </div>
      </Section>
      <Section className="mt-10">
        <div className="grid md:grid-cols-2 gap-6">
          {modules.map((m) => (
            <ModuleCard key={m.id} m={m} token={token} onCompleted={setModules} />
          ))}
        </div>
      </Section>
    </div>
  );
}
